import { Entity, Example } from '../types';

/**
 * BQ-13: Strict / partial / type / boundary P/R/F1 vs `humanAnnotations`.
 *
 * Match modes follow the nervaluate / MUC convention:
 *  - exact:    (start, end, label) triple equality.
 *  - partial:  any character-span overlap > 0 AND label match.
 *  - type:     any character-span overlap > 0 AND label match.
 *              In nervaluate, "type" is technically distinct from "partial"
 *              (type counts label-correct spans regardless of boundary, while
 *              partial weights partial overlaps). For our purposes — and to
 *              avoid double-counting in our simple TP/FP/FN model — type and
 *              partial use the same predicate. We retain `type` as a separate
 *              mode for future divergence and so callers can address it
 *              explicitly. UI hides it because it duplicates partial today.
 *  - boundary: exact (start, end) equality regardless of label.
 *
 * Each gold annotation is matched at most once; each prediction is matched at
 * most once. Unmatched gold = FN, unmatched prediction = FP. P/R/F1 are
 * derived from (TP, FP, FN) with safe-zero on empty denominators.
 */

export type MatchMode = 'exact' | 'partial' | 'type' | 'boundary';

export interface PRF {
  tp: number;
  fp: number;
  fn: number;
  precision: number;
  recall: number;
  f1: number;
}

export interface ModelMetrics {
  exact: PRF;
  partial: PRF;
  type: PRF;
  boundary: PRF;
  perLabel: Record<
    string,
    { exact: PRF; partial: PRF; type: PRF; boundary: PRF }
  >;
  /** True if no Example in the dataset had any humanAnnotations entries. */
  noGold: boolean;
}

const EMPTY_PRF: PRF = {
  tp: 0,
  fp: 0,
  fn: 0,
  precision: 0,
  recall: 0,
  f1: 0,
};

const PERFECT_PRF: PRF = {
  tp: 0,
  fp: 0,
  fn: 0,
  precision: 1,
  recall: 1,
  f1: 1,
};

function hasOffsets(e: Entity): e is Entity & { start: number; end: number } {
  return typeof e.start === 'number' && typeof e.end === 'number';
}

function spansOverlap(a: { start: number; end: number }, b: { start: number; end: number }): boolean {
  // Half-open intervals [start, end). Overlap iff a.start < b.end AND b.start < a.end.
  return a.start < b.end && b.start < a.end;
}

function predicate(
  gold: Entity & { start: number; end: number },
  pred: Entity & { start: number; end: number },
  mode: MatchMode
): boolean {
  switch (mode) {
    case 'exact':
      return gold.start === pred.start && gold.end === pred.end && gold.label === pred.label;
    case 'partial':
    case 'type':
      return spansOverlap(gold, pred) && gold.label === pred.label;
    case 'boundary':
      return gold.start === pred.start && gold.end === pred.end;
  }
}

function finalize(tp: number, fp: number, fn: number): PRF {
  const precision = tp + fp === 0 ? 0 : tp / (tp + fp);
  const recall = tp + fn === 0 ? 0 : tp / (tp + fn);
  const f1 = precision + recall === 0 ? 0 : (2 * precision * recall) / (precision + recall);
  return { tp, fp, fn, precision, recall, f1 };
}

/**
 * Compute P/R/F1 for a single (gold, predictions) pair under one mode.
 *
 * Edge cases:
 *  - gold.length === 0 && predictions.length === 0 → perfect (P=R=F1=1).
 *  - gold.length === 0 && predictions.length > 0   → all predictions are FP, P=R=F1=0.
 *  - gold.length > 0 && predictions.length === 0   → all gold are FN, P=R=F1=0.
 *
 * Entities without start/end offsets are dropped from the comparison — span
 * matching requires offsets.
 */
export function computePRF(gold: Entity[], predictions: Entity[], mode: MatchMode): PRF {
  const goldSpans = gold.filter(hasOffsets);
  const predSpans = predictions.filter(hasOffsets);

  if (goldSpans.length === 0 && predSpans.length === 0) {
    return { ...PERFECT_PRF };
  }

  const goldMatched = new Array<boolean>(goldSpans.length).fill(false);
  const predMatched = new Array<boolean>(predSpans.length).fill(false);

  // Greedy left-to-right match: each prediction takes the first unmatched gold
  // it satisfies the predicate with. This avoids double-counting on either side.
  for (let pi = 0; pi < predSpans.length; pi++) {
    for (let gi = 0; gi < goldSpans.length; gi++) {
      if (goldMatched[gi]) continue;
      if (predicate(goldSpans[gi], predSpans[pi], mode)) {
        goldMatched[gi] = true;
        predMatched[pi] = true;
        break;
      }
    }
  }

  const tp = predMatched.filter(Boolean).length;
  const fp = predMatched.length - tp;
  const fn = goldMatched.length - goldMatched.filter(Boolean).length;
  return finalize(tp, fp, fn);
}

/**
 * Aggregate P/R/F1 across all examples for one model — micro-averaged.
 *
 * Micro-averaging: TP/FP/FN are summed across all examples, then P/R/F1 are
 * computed once on the totals. Per-label breakdown follows the same rule,
 * scoped to entities of that label (gold-side label for FN; pred-side label
 * for FP/TP under exact/partial/type — for boundary mode FP/FN are bucketed
 * by their own side's label since labels are not part of the predicate).
 */
export function computeModelMetrics(examples: Example[], modelName: string): ModelMetrics {
  const modes: MatchMode[] = ['exact', 'partial', 'type', 'boundary'];

  // Aggregate counters per mode (overall and per-label).
  const overall: Record<MatchMode, { tp: number; fp: number; fn: number }> = {
    exact: { tp: 0, fp: 0, fn: 0 },
    partial: { tp: 0, fp: 0, fn: 0 },
    type: { tp: 0, fp: 0, fn: 0 },
    boundary: { tp: 0, fp: 0, fn: 0 },
  };
  const perLabelCounts: Record<string, Record<MatchMode, { tp: number; fp: number; fn: number }>> = {};

  const ensureLabelBucket = (label: string) => {
    if (!perLabelCounts[label]) {
      perLabelCounts[label] = {
        exact: { tp: 0, fp: 0, fn: 0 },
        partial: { tp: 0, fp: 0, fn: 0 },
        type: { tp: 0, fp: 0, fn: 0 },
        boundary: { tp: 0, fp: 0, fn: 0 },
      };
    }
    return perLabelCounts[label];
  };

  let anyGold = false;

  for (const ex of examples) {
    const gold = (ex.humanAnnotations ?? []).filter(hasOffsets);
    if (gold.length > 0) anyGold = true;
    const response = ex.modelResponses.find((r) => r.modelName === modelName);
    const preds = (response?.entities ?? []).filter(hasOffsets);

    // BQ-15: build a set of (start-end-label) keys for predictions the user
    // explicitly rejected for THIS model on THIS example. Rejected
    // predictions are excluded from TP/FP/FN counting — the user has
    // declared them out-of-scope, so they should not penalize the model.
    const rejected = new Set<string>();
    for (const r of ex.rejectedPredictions ?? []) {
      if (r.modelName !== modelName) continue;
      const e = r.entity;
      if (!hasOffsets(e)) continue;
      rejected.add(`${e.start}-${e.end}-${e.label}`);
    }
    const isRejected = (p: Entity & { start: number; end: number }) =>
      rejected.has(`${p.start}-${p.end}-${p.label}`);

    for (const mode of modes) {
      const goldMatched = new Array<boolean>(gold.length).fill(false);
      const predMatched = new Array<boolean>(preds.length).fill(false);

      for (let pi = 0; pi < preds.length; pi++) {
        if (isRejected(preds[pi])) continue;
        for (let gi = 0; gi < gold.length; gi++) {
          if (goldMatched[gi]) continue;
          if (predicate(gold[gi], preds[pi], mode)) {
            goldMatched[gi] = true;
            predMatched[pi] = true;
            break;
          }
        }
      }

      // Update overall and per-label counts. Rejected predictions are
      // skipped entirely on the prediction side — they neither earn TP
      // (already excluded from matching above) nor count as FP.
      for (let pi = 0; pi < preds.length; pi++) {
        if (isRejected(preds[pi])) continue;
        const label = preds[pi].label;
        const bucket = ensureLabelBucket(label);
        if (predMatched[pi]) {
          overall[mode].tp += 1;
          bucket[mode].tp += 1;
        } else {
          overall[mode].fp += 1;
          bucket[mode].fp += 1;
        }
      }
      for (let gi = 0; gi < gold.length; gi++) {
        if (goldMatched[gi]) continue;
        const label = gold[gi].label;
        const bucket = ensureLabelBucket(label);
        overall[mode].fn += 1;
        bucket[mode].fn += 1;
      }
    }
  }

  const finalizeAll = (
    counts: Record<MatchMode, { tp: number; fp: number; fn: number }>
  ): { exact: PRF; partial: PRF; type: PRF; boundary: PRF } => ({
    exact: finalize(counts.exact.tp, counts.exact.fp, counts.exact.fn),
    partial: finalize(counts.partial.tp, counts.partial.fp, counts.partial.fn),
    type: finalize(counts.type.tp, counts.type.fp, counts.type.fn),
    boundary: finalize(counts.boundary.tp, counts.boundary.fp, counts.boundary.fn),
  });

  const overallMetrics = finalizeAll(overall);
  const perLabel: ModelMetrics['perLabel'] = {};
  for (const label of Object.keys(perLabelCounts)) {
    perLabel[label] = finalizeAll(perLabelCounts[label]);
  }

  // If no gold annotations exist anywhere in the dataset, there is nothing
  // meaningful to report; return zero metrics with the noGold flag set.
  if (!anyGold) {
    return {
      exact: { ...EMPTY_PRF },
      partial: { ...EMPTY_PRF },
      type: { ...EMPTY_PRF },
      boundary: { ...EMPTY_PRF },
      perLabel: {},
      noGold: true,
    };
  }

  return {
    ...overallMetrics,
    perLabel,
    noGold: false,
  };
}
